import {
  CreateEstimate,
  CreateSubscription,
  Customer,
  Intent,
  StoreSubscription,
  Subscription,
  TermEstimate,
  SignupEstimate
} from '@futrli-sdk/billing';
import { Organisation, ProductSetup } from '@futrli-sdk/organisations';
import { Permission, Workspace } from '@futrli-sdk/workspaces';
import {
  API_PATHS,
  interpolatePathParams,
  REQUEST_METHODS,
  ROUTE_PARAMS,
  ProductSlug
} from '@futrli/shared-services';
import { AxiosResponse } from 'axios';

import { APIClients } from '@futrli/shared-services';
import { INTEGRATION_LEVEL } from './constants';
import { CreateCustomer, PaymentIntentCreate } from './types';
import { QueryParams } from '@futrli/predict/core/types';

/**
 * Gets the data from an AxiosResponse.
 * @param {getAxiosResponseData<T>} response - Gets an axios response and return the data.
 * @returns {T} - Response data.
 */
const getAxiosResponseData = <T>({ data }: AxiosResponse<T>): T => data;

/**
 * Fetches a ProductSetup.
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} productSetupId
 * @returns {Promise<ProductSetup>}
 */
export const fetchProductSetup = async (
  workspaceId: string,
  organisationId: string,
  productSetupId: string
): Promise<ProductSetup> => {
  const path = interpolatePathParams(API_PATHS.PRODUCT_SETUP_BY_ID, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SETUP_ID]: productSetupId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });
  return getAxiosResponseData(response.data);
};

/**
 * Fetches a list of organisations for a workspace.
 *
 * @param {string} workspaceId
 * @returns {Promise<Organisation[]>}
 */
export const fetchOrganisations = async (workspaceId: string): Promise<Organisation[]> => {
  const path = interpolatePathParams(API_PATHS.ORGANISATIONS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });
  return getAxiosResponseData(response.data);
};

/**
 * Fetches an organisation.
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @returns {Promise<Organisation>}
 */
export const fetchOrganisationById = async (workspaceId: string, organisationId: string): Promise<Organisation> => {
  const path = interpolatePathParams(API_PATHS.ORGANISATION_BY_ID, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });
  return getAxiosResponseData(response.data);
};

/**
 * Fetches a workspace.
 *
 * @param {string} workspaceId
 * @returns {Promise<Workspace>}
 */
export const fetchWorkspaceById = async (workspaceId: string): Promise<Workspace> => {
  const path = interpolatePathParams(API_PATHS.WORKSPACE_BY_ID, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });
  return getAxiosResponseData(response.data);
};

/**
 * Fetches the workspace permissions for a user.
 *
 * @param {string} workspaceId
 * @returns {Promise<PermissionsWrapper>}
 */
export const fetchWorkspacePermissions = async (workspaceId: string): Promise<Permission[]> => {
  const path = interpolatePathParams(API_PATHS.WORKSPACE_PERMISSIONS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });
  return getAxiosResponseData(response.data);
};

/**
 * Fetches the signup estimate
 * @param {string} workspaceId
 * @returns {Promise<SignupEstimate>}
 */
export const fetchSignupEstimates = async (
  workspaceId: string,
  queryParams: QueryParams = {}
): Promise<SignupEstimate> => {
  const path = interpolatePathParams(API_PATHS.SIGNUP_ESTIMATES, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path,
        params: queryParams
      }
    }
  });

  return response.data;
};

/**
 * Fetchs the estimate product cost for an specific integration level with coupons applied.
 * @param {string} workspaceId
 * @param {string} productSlug
 * @param {string} integrationLevel
 * @param {CreateEstimate} payload
 * @returns {Promise<TermEstimate[]>}
 */
export const fetchEstimateWithCoupons = async (
  workspaceId: string,
  productSlug: ProductSlug,
  integrationLevel: string = INTEGRATION_LEVEL,
  payload: Partial<CreateEstimate>
): Promise<TermEstimate> => {
  const path = interpolatePathParams(API_PATHS.ESTIMATES, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.PRODUCT_SLUG]: productSlug,
    [ROUTE_PARAMS.INTEGRATION_LEVEL]: integrationLevel
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.POST,
        url: path,
        data: payload
      }
    }
  });

  return response.data;
};

/**
 * Fetchs the current user subscription.
 * @param {string} workspaceId
 * @returns {Promise<Subscription>}
 */
export const fetchSubscription = async (workspaceId: string): Promise<Subscription | undefined> => {
  const path = interpolatePathParams(API_PATHS.SUBSCRIPTIONS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });
  return response.data[0];
};

/**
 * Creates a new setup intent to create a new payment source for a customer.
 * @param {string} workspaceId
 * @returns {Promise<Intent>}
 */
export const createSetupIntent = async (workspaceId: string): Promise<Intent> => {
  const path = interpolatePathParams(API_PATHS.SETUP_INTENTS, { [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId });

  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.PUT,
        url: path
      }
    }
  });

  return response.data;
};

/**
 * Handles the change in state of an existing setup intent.
 * @param {string} workspaceId
 * @param {string} intentId
 * @returns {Promise<Intent>}
 */
export const handleSetupIntent = async (workspaceId: string, intentId: string): Promise<Intent> => {
  const path = interpolatePathParams(API_PATHS.SETUP_INTENTS, { [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId });

  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.POST,
        url: path,
        data: {
          id: intentId
        }
      }
    }
  });

  return response.data;
};

/**
 * Creates a new payment intent with a specific amount.
 * @param {string} workspaceId
 * @param {PaymentIntentPayload} payload
 * @returns {Promise<Intent>}
 */
export const createPaymentIntent = async (workspaceId: string, payload: PaymentIntentCreate): Promise<Intent> => {
  const path = interpolatePathParams(API_PATHS.PAYMENT_INTENTS, { [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId });

  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.PUT,
        url: path,
        data: payload
      }
    }
  });

  return response.data;
};

/**
 * Handles the change in state of an existing payment intent.
 * @param {string} workspaceId
 * @param {string} intentId
 * @returns {Promise<Intent>}
 */
export const handlePaymentIntent = async (workspaceId: string, intentId: string): Promise<Intent> => {
  const path = interpolatePathParams(API_PATHS.PAYMENT_INTENTS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.POST,
        url: path,
        data: {
          id: intentId
        }
      }
    }
  });

  return response.data;
};

/**
 * Creates a new subscription for a user.
 * @param {string} workspaceId
 * @param {CreateSubscription} payload - data needed to create the subscription.
 * @returns {Promise<Subscription>}
 */
export const createSubscription = async (
  workspaceId: string,
  payload: Partial<CreateSubscription>
): Promise<Subscription> => {
  const path = interpolatePathParams(API_PATHS.SUBSCRIPTIONS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.PUT,
        url: path,
        data: payload
      }
    }
  });
  return response.data;
};

/**
 * Creates a new subscription for a user.
 * @param {string} workspaceId
 * @param {CreateSubscription} payload - data needed to create the subscription.
 * @returns {Promise<Subscription>}
 */
export const activateSubscription = async (
  workspaceId: string,
  payload: Partial<CreateSubscription>
): Promise<Subscription> => {
  const path = interpolatePathParams(API_PATHS.SUBSCRIPTIONS_ACTIVATE, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.POST,
        url: path,
        data: payload
      }
    }
  });
  return response.data;
};

/**
 * Creates a new customer in the billing subscription system.
 * @param {string} workspaceId
 * @param {CreateCustomer} payload
 * @returns {Promise<Customer>}
 */
export const createCustomer = async (workspaceId: string, payload?: CreateCustomer): Promise<Customer> => {
  const path = interpolatePathParams(API_PATHS.CUSTOMERS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.PUT,
        url: path,
        data: payload
      }
    }
  });
  return response.data;
};

/**
 * Gets user subscription details.
 * @param {string} workspaceId
 * @returns {Promise<Customer>}
 */
export const fetchStoredSubscriptionData = async (workspaceId: string): Promise<StoreSubscription> => {
  const path = interpolatePathParams(API_PATHS.SUBSCRIPTIONS_STORE, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });
  return response.data;
};

/**
 * Converts an invited organisation which the user has been given access into, to an own organisation,
 * changing the owner to the one who makes the request (the invited one and futire owner).
 * @param {string} workspaceId
 * @returns {Promise<Customer>}
 */
export const updateOrganisationOwnership = async (
  workspaceId: string,
  organisationId: string
): Promise<Organisation> => {
  const path = interpolatePathParams(API_PATHS.CHANGE_ORGANISATION_OWNERSHIP, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.POST,
        url: path
      }
    }
  });
  return response.data;
};
